import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tag, Loader2 } from "lucide-react";

export const PrixVenteDialog = ({ sid }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [prix, setPrix] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows(null);
    api.get("/etat-stock/prix-vente", { params: { societe_id: sid } }).then((r) => {
      setRows(r.data);
      setPrix(Object.fromEntries(r.data.map((p) => [p.nom, String(p.prix_vente)])));
    });
  }, [open, sid]);

  const save = async () => {
    const changes = Object.fromEntries(rows.filter((p) => Number(String(prix[p.nom]).replace(",", ".")) !== p.prix_vente)
      .map((p) => [p.nom, Number(String(prix[p.nom]).replace(",", "."))]));
    if (Object.values(changes).some((v) => Number.isNaN(v) || v < 0)) return toast.error("Prix invalide");
    setSaving(true);
    try {
      const r = await api.put("/etat-stock/prix-vente", { societe_id: sid, prix: changes });
      toast.success(`${r.data.modifies} prix de vente mis à jour`);
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button variant="outline" className="gap-2" onClick={() => setOpen(true)} data-testid="prix-vente-open-btn"><Tag className="h-4 w-4" /> Prix de vente</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="prix-vente-dialog">
          <DialogHeader>
            <DialogTitle>Prix de vente unitaires HT</DialogTitle>
            <DialogDescription>S'applique aux prochaines factures. Les factures déjà émises (et le CA de l'état de stock) ne changent pas.</DialogDescription>
          </DialogHeader>
          {!rows ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
            <table className="w-full text-sm">
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className="border-b last:border-0" data-testid={`prix-vente-row-${p.nom}`}>
                    <td className="py-1.5 font-medium">{p.nom}</td>
                    <td className="w-40"><Input className="h-8 text-right" inputMode="decimal" value={prix[p.nom] ?? ""} onChange={(ev) => setPrix((s) => ({ ...s, [p.nom]: ev.target.value }))} data-testid={`prix-vente-input-${p.nom}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="prix-vente-cancel-btn">Annuler</Button>
            <Button onClick={save} disabled={!rows || saving} data-testid="prix-vente-save-btn">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
